/**
 * Answer comparison shared by every grammar course.
 *
 * The one decision that differs between languages is what a diacritic means,
 * and it differs completely. In German a missing umlaut is usually the answer
 * being wrong — `fahrst` vs `fährst` is the whole content of a lesson — so the
 * normalizer transliterates (`ä` → `ae`) and never folds to the bare vowel. In
 * Portuguese a missing accent is usually a phone keyboard, so the normalizer
 * folds by default and only an exercise about the accent turns folding off.
 *
 * That decision lives in each course's own `answer.ts`, next to the reasoning.
 * What lives here is everything downstream of it: how a normalized string is
 * compared to a list, and how a tapped sequence of chunks is compared to a set
 * of accepted orders. Neither has a language in it, and a bug in either should
 * be fixed once rather than twice.
 *
 * What no normalizer here may do is accept an edit distance. See the note in
 * `src/apps/grammatik/answer.ts`: typo tolerance applied to grammar marks both
 * sides of the distinction correct and the deck stops measuring anything.
 */

/**
 * A course's normalizer.
 *
 * `strict` is the per-exercise opt-out — `case_sensitive` in German,
 * `accent_sensitive` in Portuguese. It means "this exercise is about the thing
 * you would otherwise forgive", so forgive less.
 */
export type GrammarNormalizer = (text: string, strict?: boolean) => string;

/**
 * Is `input` one of `accepted`?
 *
 * `accepted` is the exercise's `answer` plus its `accept` list. That list is
 * not a convenience: both languages frequently permit more than one phrasing,
 * and an exercise that admits only the first teaches that the second is wrong.
 * Authoring is responsible for completeness; this only decides membership.
 */
export function isGrammarAnswerCorrect(
  normalize: GrammarNormalizer,
  input: string,
  accepted: string[],
  strict = false,
): boolean {
  if (!input.trim() || accepted.length === 0) return false;
  const normalized = normalize(input, strict);
  return accepted.some((candidate) => normalize(candidate, strict) === normalized);
}

/**
 * Compare an `order` answer: the chunks the user tapped, in sequence.
 *
 * Separate from the free-text path because the unit of comparison is a chunk,
 * not a character. Joining the chunks into a sentence and reusing the text
 * comparison would work today and break the moment two chunks differ only by
 * the space between them.
 *
 * Never strict, in either language. Reordering a sentence moves the capital —
 * *Ich muss heute arbeiten* and *Heute muss ich arbeiten* are the same chunks —
 * so a case-sensitive permutation check would reject exactly the alternative
 * orders `also_accept` exists to hold.
 */
export function isOrderCorrect(
  normalize: GrammarNormalizer,
  input: string[],
  accepted: string[][],
): boolean {
  if (input.length === 0) return false;
  const normalized = input.map((chunk) => normalize(chunk)).join(' ');
  return accepted.some(
    (candidate) =>
      candidate.length === input.length &&
      candidate.map((chunk) => normalize(chunk)).join(' ') === normalized,
  );
}
